import { useState, useEffect } from "react";
import {
    Grid,
    Stack,
    Box,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import SnackbarMessage from "../../components/snackBar/snackBar";
import { updateContactThunk, getContactThunk, getIndividualUserThunk, getTagsThunk } from '../contact/contactThunk'
import { getMyTaskThunk, getLeadManagementThunk } from "../leadMangement/leadMangementThunk";
import { getChatContacts } from "../messages/messagesThunk";
import { getFileUsersThunk } from "../files/filesThunk";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import { FieldTextField, FieldMultiSelect } from "../../components/selections/fieldInputs";
import { QR, QrActionButton } from "../qrCode/components/qrUi";


export default function ContactEdit({ handleClose, show, contact_id, updationType }) {
    const dispatch = useDispatch();

    const [state, setState] = useState({
        userName: "",
        userEmail: "",
        userMobile: "",
        profileTags: [],
        organization: [],
        branchDepartmentValue: [],
        errors: {},
        isEdit: true,
    });

    const [changedFields, setChangedFields] = useState(new Set());

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const { getOrganizations, getTags, getUserProfile, status, error } = useSelector(
        (state) => state.contacts
    );

    const { branchDepartments } = useSelector((state) => state.general);

    // Reset state when modal closes
    useEffect(() => {
        if (!show) {
            setState({
                userName: "",
                userEmail: "",
                userMobile: "",
                profileTags: [],
                organization: [],
                branchDepartmentValue: [],
                errors: {},
                isEdit: true
            });
            setChangedFields(new Set());
        }
    }, [show]);

    // Populate state from API
    useEffect(() => {
        if (getUserProfile?.userdata) {
            const { name, email, mobile } = getUserProfile.userdata;
            setState((prev) => ({
                ...prev,
                userName: name || "",
                userEmail: email || "",
                userMobile: mobile || "",
            }));
        }
    }, [getUserProfile]);

    // Populate multi-selects
    useEffect(() => {
        if (getUserProfile?.userdata?.tag_id && getTags?.length) {
            const tagIds = getUserProfile.userdata.tag_id
                .split(",")
                .map((id) => parseInt(id.trim(), 10));
            const selectedTags = getTags
                .filter((tag) => tagIds.includes(tag.id))
                .map((tag) => ({ value: tag.id, label: tag.tag_name }));
            setState((prev) => ({ ...prev, profileTags: selectedTags }));
        } else {
            setState((prev) => ({ ...prev, profileTags: [] }));
        }
    }, [getUserProfile, getTags]);

    useEffect(() => {
        if (
            getUserProfile?.userdata?.branch_department_id &&
            branchDepartments?.length
        ) {
            const branchDeptIds = getUserProfile.userdata.branch_department_id
                .split(",")
                .map((id) => parseInt(id.trim(), 10));
            const selectedBranchDepts = branchDepartments
                .filter((bd) => branchDeptIds.includes(bd.id))
                .map((bd) => ({
                    value: bd.id,
                    label: `${bd.branch_name} - ${bd.department_name}`,
                }));
            setState((prev) => ({ ...prev, branchDepartmentValue: selectedBranchDepts }));
        } else {
            setState((prev) => ({ ...prev, branchDepartmentValue: [] }));
        }
    }, [getUserProfile, branchDepartments]);

    useEffect(() => {
        if (getUserProfile?.userdata?.organization_ids && getOrganizations?.length) {
            const orgIds = getUserProfile.userdata.organization_ids
                .split(",")
                .map((id) => parseInt(id.trim(), 10));
            const selectedOrgs = getOrganizations
                .filter((org) => orgIds.includes(org.id))
                .map((org) => ({ value: org.id, label: org.organization_name }));
            setState((prev) => ({ ...prev, organization: selectedOrgs }));
        } else {
            setState((prev) => ({ ...prev, organization: [] }));
        }
    }, [getUserProfile, getOrganizations]);

    // Handlers
    const handleChange = (eOrName, maybeValue) => {
        let name, value;
        if (typeof eOrName === "string") {
            name = eOrName;
            value = maybeValue;
        } else {
            name = eOrName.target.name;
            value = eOrName.target.value;
            if (value.startsWith(" ")) return;
        }

        setState((prev) => ({
            ...prev,
            [name]: value,
            errors: { ...prev.errors, [name]: "" },
        }));

        setChangedFields((prev) => new Set(prev).add(name));
    };

    const handleSingleSelectChange = (name, value) => {
        setState((prev) => ({
            ...prev,
            [name]: value,
            errors: { ...prev.errors, [name]: "" },
        }));
        setChangedFields((prev) => new Set(prev).add(name));
    };

    const handleMultiSelectChange = (name, values) => {
        setState((prev) => ({
            ...prev,
            [name]: values,
            errors: { ...prev.errors, [name]: "" },
        }));
        setChangedFields((prev) => new Set(prev).add(name));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!changedFields.size) {
            setSnackbar({
                open: true,
                message: "No changes to update",
                severity: "info",
            });

            setTimeout(() => {
                handleClose();
            }, 1000);

            return;
        }

        const payload = {};

        if (changedFields.has("userName")) payload.name = state.userName;
        if (changedFields.has("userEmail")) payload.email = state.userEmail;
        if (changedFields.has("branchDepartmentValue"))
            payload.branch_department_id = state.branchDepartmentValue
                .map((item) => item.value)
                .toString();
        if (changedFields.has("organization"))
            payload.organization_id = state.organization.map((item) => item.value).toString();
        if (changedFields.has("profileTags"))
            payload.tag_id = state.profileTags.map((item) => item.value).toString();

        const payload1 = { ...payload, wa_contact_id: getUserProfile?.userdata?.id };

        try {
            const result = await dispatch(updateContactThunk({ credentials: payload1 }));
            if (result.meta.requestStatus === "fulfilled") {
                setSnackbar({
                    open: true,
                    message: result.payload.message,
                    severity: "success",
                });
                dispatch(getTagsThunk({ type: "tags" }));
                dispatch(getContactThunk({ type: "getContact" }));
                dispatch(getIndividualUserThunk({ wa_contact_id: contact_id }));

                if (updationType === "myTask") {
                    await dispatch(getMyTaskThunk({ type: "getTask" }));
                } else if (updationType === "leadManagement") {
                    await dispatch(getLeadManagementThunk({ type: "getLeads" }));
                } else if (updationType === "waContacts") {
                    await dispatch(getChatContacts());
                } else if (updationType === "fileContacts") {
                    await dispatch(getFileUsersThunk({ type: "getUserLevelData" }));
                }

                handleClose();
            } else {
                setSnackbar({
                    open: true,
                    message: result.payload,
                    severity: "error",
                });
            }
        } catch {
            setSnackbar({
                open: true,
                message: "Server error. Please try again.",
                severity: "error",
            });
        }
    };

    const fields = [
        { id: 1, type: "text", name: "userMobile", label: "Mobile", },
        { id: 2, type: "text", name: "userName", label: "Name" },
        {
            id: 3,
            type: "multi-select",
            name: "branchDepartmentValue",
            label: "Branch Department",
            options: branchDepartments.map((item) => ({
                value: item.id,
                label: `${item.branch_name} - ${item.department_name}`,
            })),
        },
        {
            id: 4,
            type: "multi-select",
            name: "profileTags",
            label: "Tags",
            options: getTags.map((item) => ({ value: item.id, label: item.tag_name })),
        },
        {
            id: 5,
            type: "multi-select",
            name: "organization",
            label: "Organizations",
            options: getOrganizations.map((item) => ({
                value: item.id,
                label: item.organization_name,
            })),
        },
    ];

    // 🔹 Snackbar for errors
    useEffect(() => {
        if (status === "failed") {
            setSnackbar({
                open: true,
                message: error,
                severity: "error",
            });
        }
    }, [status, error]);

    const hadleEdit = () => {
        setState(prev => ({ ...prev, isEdit: true }))
    };

    // QR-style fields: label above, icon in the box
    const FIELD_ICONS = {
        userMobile: <PhoneIphoneOutlinedIcon />,
        userName: <PersonOutlineOutlinedIcon />,
        branchDepartmentValue: <ApartmentRoundedIcon />,
        profileTags: <LocalOfferOutlinedIcon />,
        organization: <BusinessOutlinedIcon />,
    };
    const FIELD_PLACEHOLDERS = {
        userName: "Enter name",
        branchDepartmentValue: "Select branch department",
        profileTags: "Select tags",
        organization: "Select organizations",
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Grid container columnSpacing={2.5} rowSpacing={2.5}>
                {fields.map((field) => (
                    <Grid key={field.id} size={{ xs: 12, sm: 6, md: 4 }}>
                        {field.type === "text" && (
                            <FieldTextField
                                name={field.name}
                                label={field.label}
                                value={state[field.name]}
                                onChange={handleChange}
                                maxLength={100}
                                icon={FIELD_ICONS[field.name]}
                                placeholder={FIELD_PLACEHOLDERS[field.name]}
                                error={state.errors?.[field.name]}
                                disabled={field.name === "userMobile" ? true : !state.isEdit}
                            />
                        )}

                        {field.type === "multi-select" && (
                            <FieldMultiSelect
                                name={field.name}
                                label={field.label}
                                options={field.options}
                                value={state[field.name] || []}
                                onChange={handleMultiSelectChange}
                                icon={FIELD_ICONS[field.name]}
                                placeholder={FIELD_PLACEHOLDERS[field.name]}
                                error={state.errors?.[field.name]}
                                disabled={!state.isEdit}
                            />
                        )}
                    </Grid>
                ))}
            </Grid>

            {/* Footer */}
            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 4, pt: 2.5, borderTop: `1px solid ${QR.border}`, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } } }}>
                <QrActionButton
                    variant="solid"
                    color={QR.main}
                    gradientTo={QR.light}
                    icon={state.isEdit === false ? <EditOutlinedIcon sx={{ fontSize: 17 }} /> : <SaveOutlinedIcon sx={{ fontSize: 17 }} />}
                    label={state.isEdit === false ? "Edit" : "Update Contact"}
                    loading={status === "loading"}
                    loadingLabel="Saving..."
                    onClick={(e) => (state.isEdit === false ? hadleEdit() : handleSubmit(e))}
                />
            </Stack>

            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            />
        </Box>
    );
};